/**
 * StreamingMessage - Optimized message component for token-by-token streaming
 * 
 * Prevents layout thrashing by:
 * - Reserving estimated height upfront
 * - Throttling height growth notifications
 * - Using transform-only animations
 * - Minimizing reflows during streaming
 */
import React, { useRef, useLayoutEffect, useCallback, useState, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useThrottled } from './hooks/useScrollOrchestrator';

/**
 * Calculate estimated height for message content
 * @param {string} text - Message text
 * @param {Object} options - Calculation options
 * @returns {number} Estimated height in pixels
 */
function estimateMessageHeight(text, options = {}) {
  const {
    lineHeight = 24, // Approximate line height in pixels
    avgCharsPerLine = 80, // Average characters per line
    minHeight = 48, // Minimum height (2 lines)
    maxInitialHeight = 240, // Max initial reservation (10 lines)
    hasMarkdown = true // Account for markdown formatting
  } = options;
  
  if (!text) return minHeight;
  
  // Rough estimate of lines
  let estimatedLines = Math.ceil(text.length / avgCharsPerLine);
  
  // Account for markdown elements
  if (hasMarkdown) {
    // Count newlines
    const newlines = (text.match(/\n/g) || []).length;
    estimatedLines = Math.max(estimatedLines, newlines + 1);
    
    // Add extra space for headers, lists, code blocks
    if (text.includes('```')) estimatedLines += 4; // Code blocks
    if (text.includes('#')) estimatedLines += 2; // Headers
    if (text.match(/^\s*[-*+]\s/gm)) estimatedLines += 1; // Lists
  }
  
  const estimatedHeight = estimatedLines * lineHeight;
  
  // Clamp to reasonable initial range
  return Math.min(Math.max(estimatedHeight, minHeight), maxInitialHeight);
}

/**
 * StreamingMessage Component
 * Renders a message with reserved height to prevent layout shifts
 */
const StreamingMessage = memo(({
  content,
  isStreaming,
  isUser,
  isAtBottom,
  onHeightGrow,
  className = '',
  messageRef,
  renderOptions = {}
}) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const lastHeightRef = useRef(0);
  const [hasOverflowed, setHasOverflowed] = useState(false);
  
  // Throttled height growth notification
  const notifyHeightGrow = useThrottled(
    useCallback(() => {
      if (onHeightGrow && isAtBottom && isStreaming) {
        onHeightGrow();
      }
    }, [onHeightGrow, isAtBottom, isStreaming]),
    120 // Notify at most every 120ms
  );
  
  // Calculate initial reserved height
  const reservedHeight = isStreaming 
    ? estimateMessageHeight(content, {
        lineHeight: 24,
        avgCharsPerLine: 70, // Adjust based on your container width
        minHeight: isUser ? 32 : 48
      })
    : 'auto';
  
  // Monitor height changes during streaming
  useLayoutEffect(() => {
    if (!isStreaming || !containerRef.current) return;
    
    const container = containerRef.current;
    const currentHeight = container.scrollHeight;
    
    // Check if content has grown beyond reservation
    if (currentHeight > lastHeightRef.current) {
      const grew = currentHeight - lastHeightRef.current;
      
      // Mark as overflowed if exceeding initial reservation
      if (!hasOverflowed && typeof reservedHeight === 'number' && currentHeight > reservedHeight * 1.5) {
        setHasOverflowed(true);
      }
      
      // Notify parent if significant growth
      if (grew > 20) {
        notifyHeightGrow();
      }
      
      lastHeightRef.current = currentHeight;
    }
  }, [content, isStreaming, notifyHeightGrow, hasOverflowed, reservedHeight]);
  
  // Markdown component configuration
  const markdownComponents = {
    // Table styling
    table: ({ node, ...props }) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full border-collapse" {...props}>
          {props.children}
        </table>
      </div>
    ),
    thead: ({ node, ...props }) => (
      <thead className="bg-gray-800/50 border-b border-gray-700" {...props}>
        {props.children}
      </thead>
    ),
    tbody: ({ node, ...props }) => (
      <tbody className="divide-y divide-gray-700/30" {...props}>
        {props.children}
      </tbody>
    ),
    tr: ({ node, ...props }) => (
      <tr className="hover:bg-gray-800/30 transition-colors" {...props}>
        {props.children}
      </tr>
    ),
    th: ({ node, ...props }) => (
      <th className="px-4 py-2 text-left text-sm font-medium text-emerald-400 uppercase tracking-wider" {...props}>
        {props.children}
      </th>
    ),
    td: ({ node, ...props }) => (
      <td className="px-4 py-2 text-sm text-gray-200 whitespace-nowrap" {...props}>
        {props.children}
      </td>
    ),
    // Code block styling
    code: ({ node, inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      return !inline ? (
        <div className="relative group my-3">
          <pre className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-4 overflow-x-auto">
            <code className={`text-sm ${className || ''}`} {...props}>
              {children}
            </code>
          </pre>
        </div>
      ) : (
        <code className="bg-gray-800/50 px-1.5 py-0.5 rounded text-emerald-400 text-sm" {...props}>
          {children}
        </code>
      );
    },
    // Link styling
    a: ({ node, ...props }) => (
      <a 
        className="text-emerald-400 hover:text-emerald-300 underline transition-colors" 
        target="_blank" 
        rel="noopener noreferrer"
        {...props}
      >
        {props.children}
      </a>
    ),
    // List styling
    ul: ({ node, ...props }) => (
      <ul className="list-disc list-inside space-y-1 my-3 text-gray-200" {...props}>
        {props.children}
      </ul>
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal list-inside space-y-1 my-3 text-gray-200" {...props}>
        {props.children}
      </ol>
    ),
    // Paragraph styling
    p: ({ node, ...props }) => (
      <p className="my-2 leading-relaxed" {...props}>
        {props.children}
      </p>
    ),
    // Heading styling
    h1: ({ node, ...props }) => (
      <h1 className="text-2xl font-bold text-emerald-400 my-4" {...props}>
        {props.children}
      </h1>
    ),
    h2: ({ node, ...props }) => (
      <h2 className="text-xl font-semibold text-emerald-400 my-3" {...props}>
        {props.children}
      </h2>
    ),
    h3: ({ node, ...props }) => (
      <h3 className="text-lg font-medium text-emerald-400 my-2" {...props}>
        {props.children}
      </h3>
    )
  };
  
  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        if (messageRef) messageRef.current = node;
      }}
      className={`rounded-2xl px-3.5 py-2.5 md:px-4 md:py-3 transition-all duration-200 relative
        ${isUser ? 'max-w-[85%]' : 'w-full'} md:max-w-[75%]
        ${isUser
          ? "bg-[#2D6A4F]/30 text-white border border-emerald-500/30"
          : "bg-white/[0.10] text-white border border-white/[0.15]"
        } backdrop-filter backdrop-blur-sm shadow-sm
        ${className} ${isStreaming ? 'is-streaming' : ''} ${hasOverflowed ? 'has-overflowed' : ''}`}
      style={{
        minHeight: isStreaming ? reservedHeight : 'auto',
        transition: hasOverflowed ? 'min-height 0.3s ease-out' : 'none',
        boxShadow: isUser
          ? '0 1px 2px rgba(45, 106, 79, 0.1)'
          : '0 1px 2px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div
        ref={contentRef}
        className="text-[15px] md:text-[16px] font-light leading-relaxed break-words"
      >
        {isUser ? (
          // User messages are plain text
          <div className="whitespace-pre-wrap break-words">
            {content}
          </div>
        ) : (
          // Assistant messages use markdown
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
            className="prose prose-invert prose-sm max-w-none prose-p:text-inherit prose-p:leading-relaxed prose-table:text-inherit"
          >
            {content}
          </ReactMarkdown>
        )}

        {/* Streaming indicator */}
        {isStreaming && !isUser && (
          <span className="inline-block w-2 h-4 bg-emerald-400 animate-pulse ml-1" />
        )}
      </div>
    </div>
  );
});

StreamingMessage.displayName = 'StreamingMessage';

export default StreamingMessage;

/**
 * Export utilities for external use
 */
export { estimateMessageHeight };